// packages/ui-kit/src/primitives/Modal.tsx
// C3F v18 — reference: design/system/components/feedback/Modal.{jsx,prompt.md}
import { useEffect, type ReactNode } from "react";
import { useBodyScrollLock } from "../lib/useBodyScrollLock.js";
import { useEnterExit } from "../lib/useEnterExit.js";

type Size = "sm" | "md" | "lg";

interface Props {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  size?: Size;
  closeOnEscape?: boolean;
  closeOnOverlay?: boolean;
  /** Right-aligned action row (DS): pass the Buttons, the Modal lays them out. */
  footer?: ReactNode;
  children: ReactNode;
}

// DS widths: sm 420 · md 560 · lg 760.
const sizeCls: Record<Size, string> = {
  sm: "max-w-[420px]",
  md: "max-w-[560px]",
  lg: "max-w-[760px]",
};

export function Modal({
  open,
  onClose,
  title,
  description,
  size = "sm",
  closeOnEscape = true,
  closeOnOverlay = true,
  footer,
  children,
}: Props) {
  useBodyScrollLock(open);
  const { render, show } = useEnterExit(open);
  useEffect(() => {
    if (!open || !closeOnEscape) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, closeOnEscape, onClose]);

  if (!render) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className={`fixed inset-0 z-50 flex items-end justify-center bg-overlay p-0 transition-opacity ease-out motion-reduce:transition-none sm:items-center sm:p-5 ${
        show ? "opacity-100" : "opacity-0"
      }`}
      onClick={(e) => {
        if (closeOnOverlay && e.target === e.currentTarget) onClose();
      }}
    >
      {/* Mobile: bottom-sheet sliding up (transition). Desktop: px-modal keyframe
          on enter (10px rise + settle, --dur-base so reduced-motion collapses it),
          transition back out on exit. Dialog surface = elevated + subtle hairline +
          xl radius + xl shadow, per the DS reference. */}
      <div
        className={`flex max-h-[86vh] w-full ${sizeCls[size]} flex-col overflow-hidden rounded-t-xl border border-border-subtle bg-elevated text-fg-default shadow-xl transition ease-out motion-reduce:transition-none motion-reduce:transform-none sm:rounded-xl ${
          show
            ? "translate-y-0 opacity-100 sm:[animation:px-modal_var(--dur-base)_var(--ease-out)_both]"
            : "translate-y-full opacity-0 sm:translate-y-0 sm:scale-[.98]"
        }`}
      >
        {/* Grab handle: mobile bottom-sheet affordance only */}
        <div aria-hidden className="flex justify-center pb-1 pt-2 sm:hidden">
          <span className="h-1 w-11 rounded-pill bg-border-strong" />
        </div>
        {(title || description) && (
          <header className="px-5 pb-1 pt-1 sm:px-7 sm:pt-6">
            {title && <h2 className="text-h3 font-semibold tracking-h3">{title}</h2>}
            {description && <p className="mt-1.5 text-sm text-fg-muted">{description}</p>}
          </header>
        )}
        <div className="flex-1 overflow-y-auto px-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] pt-4 sm:px-7 sm:pb-6 sm:pt-4">
          {children}
        </div>
        {footer && (
          <footer className="flex flex-col-reverse gap-2.5 px-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] pt-1 sm:flex-row sm:justify-end sm:px-7 sm:pb-7">
            {footer}
          </footer>
        )}
      </div>
    </div>
  );
}
